import { handleUnAuthError } from '#/lib/handleUnAuthError'
import {
  BACKEND_BASE_URL,
  SKIP_AUTH_CHECK_ENDPOINTS,
} from '#/constants/backendAPIsConfig'
import { DEFAULT_LOCALE } from '#/constants/i18nConfig'

export async function customFetch<T>(
  endpoint: string,
  options: RequestInit & { passcode?: string; timeoutMs?: number } = {},
  isJsonResponse = true,
): Promise<T> {
  const isFormData = options.body instanceof FormData

  const isClient = typeof window !== 'undefined'
  const locale = isClient
    ? localStorage.getItem('i18nextLng') || DEFAULT_LOCALE
    : DEFAULT_LOCALE
  const token = isClient ? localStorage.getItem('token') || '' : ''

  const headers: HeadersInit = {
    ...(isFormData
      ? { Accept: 'application/json' }
      : { 'Content-Type': 'application/json', Accept: 'application/json' }),
    'accept-language': locale,
    ...(options.headers || {}),
  }

  // ─── Timeout via AbortController ─────────────────────────────────────────
  // GET: 8 s — generous for slow connections, still prevents forever-hangs.
  // Mutations (POST/PUT/PATCH/DELETE): caller can pass { timeoutMs: 20_000 }.
  const timeoutMs = options.timeoutMs ?? 8_000
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(`${BACKEND_BASE_URL}${endpoint}`, {
      ...options,
      method: options.method || 'GET',
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    // Check for any HTTP errors
    if (!response.ok) {
      let errorBody: any = {}
      try {
        errorBody = await response.json()
      } catch {
        errorBody = {}
      }
      const error = {
        message:
          errorBody.message || errorBody.error?.message || 'An error occurred',
        errorBody: errorBody.data || errorBody.error || null,
        status: response.status,
      }

      // ─── Prefix-based public endpoint check ────────────────────────────────
      // Bug fix: previous code used .includes() (exact-match). Slug/detail
      // endpoints like /student/courses/my-slug never matched, so auth errors
      // on public pages incorrectly called handleUnAuthError.
      const isPublicEndpoint = SKIP_AUTH_CHECK_ENDPOINTS.some(
        (prefix) =>
          endpoint === prefix ||
          endpoint.startsWith(prefix + '/') ||
          endpoint.startsWith(prefix + '?'),
      )
      if (isClient && !isPublicEndpoint) handleUnAuthError(error.status)

      throw error
    }

    // If the response is not JSON, return it as is
    if (!isJsonResponse) {
      return response as unknown as T
    }

    const json = await response.json()

    // Unwrap NestJS TransformInterceptor standard envelope { success: true, data: ..., meta?: ... }
    if (
      json &&
      typeof json === 'object' &&
      'success' in json &&
      'data' in json
    ) {
      if ('meta' in json && json.meta) {
        return { data: json.data, meta: json.meta } as unknown as T
      }
      return json.data as T
    }

    return json as T
  } catch (err: any) {
    clearTimeout(timeoutId)

    // Convert AbortError → structured timeout error with status 408
    // so route loaders can distinguish timeout from "resource not found"
    if (err?.name === 'AbortError') {
      throw {
        message: `Request timed out after ${timeoutMs / 1000}s`,
        status: 408,
        errorBody: null,
      }
    }

    throw err
  }
}
